// src/modules/app-views/account/pages/Subscription/tabs/VoipGotoTab/VoipGotoCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import { Card, Tag, Spin, Descriptions, Collapse, Grid, Alert, Button } from 'antd';
import { PhoneOutlined, ReloadOutlined } from '@ant-design/icons';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import { parsePhoneToDisplay } from 'utils/helpers';
import { openModal } from 'components/core/GenericModal';
import VoipGotoAuthModal from '../../modals/VoipGotoAuthModal';
import dayjs from 'dayjs';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VoipGotoCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [connectionData, setConnectionData] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [data] = await axiosService.get({ url: API.GOTO.CONNECTION_STATUS, returnAsObject: true });
      setConnectionData(data?.connected ? data : null);
    } catch {
      setConnectionData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleReconnect = useCallback(() => {
    openModal(VoipGotoAuthModal, {
      onSuccess: () => {
        fetchStatus();
      },
    });
  }, [fetchStatus]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isConnected = !!connectionData;
  // Backend returns { connected, data: { email, accountKey, lines, phoneNumbers, connectedAt, ... } }
  const info = connectionData?.data;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const cardTitle = (
    <div className="text-muted" style={{ display: 'flex', alignItems: 'center' }}>
      <PhoneOutlined style={{ marginRight: 8 }} />
      {translateX('goto_voip_config')}
    </div>
  );

  const descProps = isMobile
    ? { layout: 'vertical', column: 1, size: 'small', bordered: true }
    : { column: 1, size: 'small', bordered: true, styles: { label: { width: 160, minWidth: 160 }, content: { minWidth: 0 } } };

  return (
    <Card title={cardTitle} className="no-shadow">
      <Spin spinning={loading}>
        {!loading && !isConnected && (
          <Alert
            type="warning"
            showIcon
            message={translateX('goto_not_connected')}
            description={translateX('goto_token_expired_reconnect')}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleReconnect}
              >
                {translateX('goto_reconnect')}
              </Button>
            }
          />
        )}

        {isConnected && info && (
          <>
            <Descriptions {...descProps} style={{ tableLayout: 'fixed' }}>
              {info.email && (
                <Descriptions.Item label={translateX('goto_email')}>{info.email}</Descriptions.Item>
              )}
              {info.accountKey && (
                <Descriptions.Item label={translateX('goto_account')}>
                  {info.accountKey}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <Tag color="success" style={{ margin: 0 }}>{translateX('goto_connected')}</Tag>
              </Descriptions.Item>
              {info.lines?.length > 0 && (
                <Descriptions.Item label={translateX('goto_lines_count')}>
                  <div>{info.lines.length}</div>
                  <Collapse
                    ghost
                    size="small"
                    items={[
                      {
                        key: 'lines',
                        label: (
                          <span style={{ fontSize: 12, color: '#888' }}>
                            {translateX('goto_view_details')}
                          </span>
                        ),
                        children: (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {[...info.lines].sort((a, b) => (a.number || '').localeCompare(b.number || '')).map((line, idx) => (
                              <div key={line.id || idx} style={{ fontSize: 12 }}>
                                <strong>{line.number || '-'}</strong>
                                {line.name ? ` — ${line.name}` : ''}
                              </div>
                            ))}
                          </div>
                        ),
                      },
                    ]}
                    style={{ marginTop: 4, marginLeft: -12 }}
                  />
                </Descriptions.Item>
              )}
              {info.phoneNumbers?.length > 0 && (
                // <Descriptions.Item label={translateX('goto_phone_numbers_count')}>
                <Descriptions.Item label={translateX('phones')}>
                  <div>{info.phoneNumbers.length}</div>
                  <Collapse
                    ghost
                    size="small"
                    items={[
                      {
                        key: 'phoneNumbers',
                        label: (
                          <span style={{ fontSize: 12, color: '#888' }}>
                            {translateX('goto_view_details')}
                          </span>
                        ),
                        children: (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {[...info.phoneNumbers].sort((a, b) => (a.number || '').localeCompare(b.number || '')).map((phone, idx) => (
                              <div key={phone.id || idx} style={{ fontSize: 12 }}>
                                <strong>{phone.number || '-'}</strong>
                                {phone.number ? ` — ${parsePhoneToDisplay(phone.number)}` : ''}
                              </div>
                            ))}
                          </div>
                        ),
                      },
                    ]}
                    style={{ marginTop: 4, marginLeft: -12 }}
                  />
                </Descriptions.Item>
              )}
              {info.connectedAt && (
                <Descriptions.Item label={translateX('goto_connected_at')}>
                  {dayjs(info.connectedAt).format('DD/MM/YYYY HH:mm')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Spin>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default VoipGotoCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
